/**
 * BarberDashboard Component
 *
 * Displays a dashboard with 4 key metrics for the barber:
 * - Total tickets in queue
 * - Total estimated time
 * - Next client to serve
 * - Tickets completed today
 *
 * Features:
 * - Real-time updates
 * - Glassmorphism design
 * - Responsive grid (2x2 mobile, 4x1 desktop)
 * - Dark mode support
 */

import { Users, Clock, UserCircle2, CheckCircle } from 'lucide-react';
import type { BarberStats } from '../../hooks/useBarberStats';

interface BarberDashboardProps {
  stats: BarberStats;
}

export function BarberDashboard({ stats }: BarberDashboardProps) {
  const { totalInQueue, totalTime, nextClient, completedToday } = stats;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {/* Card 1: Turnos en Cola */}
      <DashboardCard
        icon={<Users className="w-6 h-6" />}
        label="Turnos en Cola"
        value={totalInQueue.toString()}
      />

      {/* Card 2: Tiempo Total */}
      <DashboardCard
        icon={<Clock className="w-6 h-6" />}
        label="Tiempo Total"
        value={`${totalTime} min`}
      />

      {/* Card 3: Próximo Cliente */}
      <DashboardCard
        icon={<UserCircle2 className="w-6 h-6" />}
        label="Próximo Cliente"
        value={nextClient ? (nextClient.clientName || nextClient.clientEmail || `Turno #${nextClient.ticketNumber}`) : 'Ninguno'}
        subtitle={nextClient ? (nextClient.clientEmail && nextClient.clientName ? nextClient.clientEmail : `Turno #${nextClient.ticketNumber}`) : 'Cola vacía'}
      />

      {/* Card 4: Completados Hoy */}
      <DashboardCard
        icon={<CheckCircle className="w-6 h-6" />}
        label="Completados Hoy"
        value={completedToday.toString()}
      />
    </div>
  );
}

interface DashboardCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtitle?: string;
}

function DashboardCard({
  icon,
  label,
  value,
  subtitle,
}: DashboardCardProps) {
  return (
    <div className="rounded-xl p-6 bg-surface border border-hairline">
      {/* Icon */}
      <div className="mb-4 text-ink-subtle">
        {icon}
      </div>

      {/* Label */}
      <p className="text-sm font-medium text-ink-muted mb-1">
        {label}
      </p>

      {/* Value */}
      <p className="text-2xl font-semibold text-ink mb-1 tabular-nums">
        {value}
      </p>

      {/* Subtitle (optional) */}
      {subtitle && (
        <p className="text-xs text-ink-subtle">
          {subtitle}
        </p>
      )}
    </div>
  );
}
